// NodeField.jsx

import React from 'react';

// Utilities
import { coerceToInput } from 'utilities/NodeFieldCoercion';
import { canonicalizeSchemaField } from 'utilities/FieldTypes';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getFieldLabel } from 'utilities/NodeLabels';
import { buildNodeSubtitlePatch, isSubtitleFieldType } from 'utilities/NodeSubtitle';
import { resolvePurchaseOrderItemLineTotal } from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';

// Components
import NodeFieldLabel from 'node/form/fieldlabel/NodeFieldLabel';
import NodeActivityField from 'node/form/fields/activity/NodeActivityField';
import NodeAnnualDateField from 'node/form/fields/annualdate/NodeAnnualDateField';
import NodeBarcodeField from 'node/form/fields/barcode/NodeBarcodeField';
import NodeBooleanField from 'node/form/fields/boolean/NodeBooleanField';
import NodeChecklistField from 'node/form/fields/checklist/NodeChecklistField';
import NodeColorField from 'node/form/fields/color/NodeColorField';
import NodeCommentsField from 'node/form/fields/comments/NodeCommentsField';
import NodeCoverField from 'node/form/fields/cover/NodeCoverField';
import NodeMoneyField from 'node/form/fields/money/NodeMoneyField';
import NodeCurrencyField from 'node/form/fields/currency/NodeCurrencyField';
import NodeDateField from 'node/form/fields/date/NodeDateField';
import NodeDateRangeField from 'node/form/fields/daterange/NodeDateRangeField';
import NodeDateTimeField from 'node/form/fields/datetime/NodeDateTimeField';
import NodeDateTimeRangeField from 'node/form/fields/datetimerange/NodeDateTimeRangeField';
import NodeEmailField from 'node/form/fields/email/NodeEmailField';
import NodeLocationField from 'node/form/fields/location/NodeLocationField';
import NodeMeasurementField from 'node/form/fields/measurement/NodeMeasurementField';
import NodeMultiselectField from 'node/form/fields/multiselect/NodeMultiselectField';
import NodeNodelistField from 'node/form/fields/nodelist/NodeNodelistField';
import NodeNumberField from 'node/form/fields/number/NodeNumberField';
import NodePeopleField from 'node/form/fields/people/NodePeopleField';
import NodePercentageField from 'node/form/fields/percentage/NodePercentageField';
import NodePhoneField from 'node/form/fields/phone/NodePhoneField';
import NodeRichTextField from 'node/form/fields/richtext/NodeRichTextField';
import NodeSelectField from 'node/form/fields/select/NodeSelectField';
import NodeSequenceField from 'node/form/fields/sequence/NodeSequenceField';
import NodeSignatureField from 'node/form/fields/signature/NodeSignatureField';
import NodeSubtitleField from 'node/form/fields/subtitle/NodeSubtitleField';
import NodeTagsField from 'node/form/fields/tags/NodeTagsField';
import NodeTextField from 'node/form/fields/text/NodeTextField';
import NodeUrlField from 'node/form/fields/url/NodeUrlField';

const TYPE_TO_COMPONENT = {
  annualdate: NodeAnnualDateField,
  barcode: NodeBarcodeField,
  boolean: NodeBooleanField,
  checklist: NodeChecklistField,
  currency: NodeCurrencyField,
  money: NodeMoneyField,
  date: NodeDateField,
  daterange: NodeDateRangeField,
  datetime: NodeDateTimeField,
  datetimerange: NodeDateTimeRangeField,
  email: NodeEmailField,
  measurement: NodeMeasurementField,
  enum: NodeSelectField,
  pipeline: NodeSelectField,
  multiselect: NodeMultiselectField,
  relation: NodeNodelistField,
  // Legacy alias until schemas are migrated off `nodelist`.
  nodelist: NodeNodelistField,
  number: NodeNumberField,
  people: NodePeopleField,
  percentage: NodePercentageField,
  phone: NodePhoneField,
  richtext: NodeRichTextField,
  sequence: NodeSequenceField,
  signature: NodeSignatureField,
  string: NodeTextField,
  url: NodeUrlField,
};

/**
 * NodeField
 *
 * Renders a single schema-driven field row for the node properties form,
 * including built-in special field types and generic schema fields.
 * 
 * @param {object} field - The field definition object, containing type, name, label, etc.
 * @param {object} node - The node object containing current field values.
 * @param {string} nodeTypeKey - The node's type key, used for resolving field values.
 * @param {string} language - The current language code, used for localization.
 * @param {function} onChange - Callback function to handle field value changes, called with (nodeKey, { [fieldName]: newValue }).
 * @param {function} onCoverSaved - Optional callback specifically for when a cover image is saved, called with (nodeKey, { coverUrl }).
 * @param {function} onLocationSaved - Optional callback specifically for when a location is saved.
 * @param {function} onSubtitleSaved - Optional callback specifically for when a subtitle is saved, called with (subtitle).
 * @param {function} onTagsSaved - Optional callback specifically for when tags are saved, called with (tags).
 * @param {function} onSettingsClick - Optional callback for when the settings button is clicked, called with (field).
 * @param {boolean} showSettings - Whether to show the settings button for this field.
 * @param {function} handleFieldChange - A generic field change handler, called with (field, newValue).
 * @param {object} wrapperProps - Additional props to spread onto the field wrapper div.
 * @param {boolean} [autoFocusRichText=false] - Autofocus this row when it is a richtext field.
 * @param {string|number} [autoFocusKey] - Refocus token passed through to richtext editors.
 *
 * @returns {ReactNode} NodeField.
 */
export default function NodeField({
  field,
  node,
  nodeTypeKey,
  language,
  onChange,
  onCoverSaved,
  onLocationSaved,
  onSubtitleSaved,
  onTagsSaved,
  onSettingsClick,
  showSettings,
  handleFieldChange,
  wrapperProps,
  previewMode = false,
  autoFocusRichText = false,
  autoFocusKey = '',
}) {
  const wrapperClassName = [
    'node-field',
    wrapperProps?.className,
  ]
    .filter(Boolean)
    .join(' ');

  const fieldDescription = String(field?.fieldDescription || '').trim();
  const schemaField = canonicalizeSchemaField(field) || field;
  const fieldType = String(schemaField?.type || '').trim().toLowerCase();
  const isCatalogOwnedOnAdopted = Boolean(
    node?.catalogId
    && schemaField?.workspaceField !== true
  );
  const isFieldReadOnly = previewMode
    || field?.writable === false
    || schemaField?.writable === false
    || isCatalogOwnedOnAdopted;

  const mergedWrapperProps = {
    ...wrapperProps,
    className: wrapperClassName,
    title: wrapperProps?.title ?? fieldDescription,
  };

  const safeOnChange = isFieldReadOnly ? undefined : onChange;
  const safeHandleFieldChange = isFieldReadOnly
    ? () => {}
    : (typeof handleFieldChange === 'function' ? handleFieldChange : () => {});
  const safeOnCoverSaved = isFieldReadOnly ? undefined : onCoverSaved;
  const safeOnLocationSaved = isFieldReadOnly ? undefined : onLocationSaved;
  const safeOnSubtitleSaved = isFieldReadOnly ? undefined : onSubtitleSaved;
  const safeOnTagsSaved = isFieldReadOnly ? undefined : onTagsSaved;
  const safeOnSettingsClick = previewMode ? undefined : onSettingsClick;
  const safeShowSettings = previewMode ? false : showSettings;

  const wrapField = (content) => (
    <div {...mergedWrapperProps}>
      {content}
    </div>
  );

  /**
   * Forwards settings clicks to the parent handler when provided.
   */

  const handleSettingsClick = typeof safeOnSettingsClick === 'function'
    ? () => safeOnSettingsClick(field)
    : undefined;

  // Built-in schema field types (mapped to root node fields)
  if (field.type === 'cover') {
    return wrapField(
      <>
        <NodeFieldLabel
          description={fieldDescription}
          label={getFieldLabel(field, language) || 'Cover'}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />

        <NodeCoverField
          node={node}
          readOnly={isFieldReadOnly}
          onCoverSaved={(payload) => {
            if (isFieldReadOnly) return;
            if (typeof safeOnCoverSaved === 'function') {
              safeOnCoverSaved(node?.key, payload);
              return;
            }
            if (payload?.removed) {
              safeOnChange?.(node?.key, { coverUrl: null });
            } else if (payload?.objectURL) {
              safeOnChange?.(node?.key, { coverUrl: payload.objectURL });
            }
          }}
        />
      </>
    );
  }

  if (field.type === 'color') {
    const colorValue = typeof node?.color === 'string' ? node.color : '';

    return wrapField(
      <>
        <NodeFieldLabel
          description={fieldDescription}
          label={getFieldLabel(field, language) || 'Identity Color'}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />

        <NodeColorField
          value={colorValue}
          readOnly={isFieldReadOnly}
          onChange={(nextColor) => safeOnChange?.(node?.key, { color: nextColor || null })}
        />
      </>
    );
  }

  if (field.type === 'location') {
    const locationValue = readNodeFieldRawValue(node, field, nodeTypeKey) ?? null;

    return wrapField(
      <>
        <NodeFieldLabel
          description={fieldDescription}
          label={getFieldLabel(field, language) || 'Location'}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />
        <NodeLocationField
          value={locationValue}
          readOnly={isFieldReadOnly}
          onLocationSaved={(nextLocation) => {
            if (isFieldReadOnly) return;
            safeHandleFieldChange(field, nextLocation ?? null);
            if (typeof safeOnLocationSaved === 'function') {
              safeOnLocationSaved(field, nextLocation ?? null);
            }
          }}
        />
      </>
    );
  }

  if (isSubtitleFieldType(field.type)) {
    const subtitleLabel = getFieldLabel(field, language) || 'Subtitle';
    return wrapField(
      <>
        <NodeFieldLabel
          description={fieldDescription}
          label={subtitleLabel}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />
        <NodeSubtitleField
          node={node}
          label={subtitleLabel}
          readOnly={isFieldReadOnly}
          onSubtitleSaved={(subtitle) => {
            if (isFieldReadOnly) return;
            if (typeof safeOnSubtitleSaved === 'function') {
              safeOnSubtitleSaved(subtitle);
              return;
            }
            safeOnChange?.(node?.key, buildNodeSubtitlePatch(subtitle));
          }}
        />
      </>
    );
  }

  if (field.type === 'tags') {
    return wrapField(
      <>
        <NodeFieldLabel
          description={fieldDescription}
          label={getFieldLabel(field, language) || 'Tags'}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />
        <NodeTagsField
          node={node}
          readOnly={isFieldReadOnly}
          onTagsSaved={(tags) => {
            if (isFieldReadOnly) return;
            if (typeof safeOnTagsSaved === 'function') {
              safeOnTagsSaved(tags);
              return;
            }
            safeOnChange?.(node?.key, { tags: Array.isArray(tags) ? tags : [] });
          }}
        />
      </>
    );
  }

  // COMMENTS
  if (field.type === 'comments') {
    return wrapField(
      <>
        <NodeFieldLabel
          description={fieldDescription}
          label={getFieldLabel(field, language) || 'Comments'}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />
        <NodeCommentsField 
          node={node} 
        />
      </>
    );
  }

  // ACTIVITY
  if (field.type === 'activity') {
    return wrapField(
      <>
        <NodeFieldLabel
          description={fieldDescription}
          label={getFieldLabel(field, language) || 'Activity'}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />
        <NodeActivityField node={node} />
      </>
    );
  }

  if (field.type === 'qrcode') {
    return null;
  }

  // Strict single-source reads
  let rawVal = readNodeFieldRawValue(node, schemaField, nodeTypeKey);

  // PO item Total is calculated (qty × unitPrice cents). Prefer derived value for display
  // so Peek stays correct even when the stored total is stale.
  if (
    fieldType === 'money'
    && String(schemaField?.name || '').trim().toLowerCase() === 'total'
    && String(nodeTypeKey || node?.type || '').trim().toUpperCase() === 'PURCHASE_ORDER_ITEM'
  ) {
    const derivedTotal = resolvePurchaseOrderItemLineTotal(node);
    if (derivedTotal != null) rawVal = derivedTotal;
  }

  const val = coerceToInput(rawVal, fieldType, schemaField);

  // NodeMoneyField expects minor units (cents). coerceToInput converts money to a
  // major-unit string for generic inputs; passing that into formatPrice treats
  // "30.00" as 30 cents → $0.30. Keep raw cents for the money control.
  const moneyValue = fieldType === 'money' ? rawVal : val;

  const fieldLabel = `${getFieldLabel(schemaField, language)}${schemaField.required ? ' *' : ''}`;

  const Comp = TYPE_TO_COMPONENT[fieldType];

  if (!Comp) {
    return wrapField(
      <>
        {fieldLabel && (
          <NodeFieldLabel
            description={fieldDescription}
            label={fieldLabel}
            showSettings={safeShowSettings}
            onSettingsClick={handleSettingsClick}
          />
        )}
        <NodeTextField
          node={node}
          fieldName={field.name}
          fieldLabel={fieldLabel}
          value={val}
          readOnly={isFieldReadOnly}
          onChange={(newVal) => safeHandleFieldChange(field, newVal)}
        />
      </>
    );
  }

  // Default path for other field types

  return wrapField(
    <>
      {fieldLabel && (
        <NodeFieldLabel
          description={fieldDescription}
          label={fieldLabel}
          showSettings={safeShowSettings}
          onSettingsClick={handleSettingsClick}
        />
      )}
      <Comp
        node={node}
        field={schemaField}
        fieldName={schemaField.name}
        fieldLabel={fieldLabel}
        value={fieldType === 'money' ? moneyValue : val}
        snapTo={schemaField?.snapTo}
        previewMode={previewMode || isFieldReadOnly}
        readOnly={isFieldReadOnly}
        autoFocus={fieldType === 'richtext' ? autoFocusRichText : undefined}
        autoFocusKey={fieldType === 'richtext' ? autoFocusKey : undefined}
        onChange={(newVal) => safeHandleFieldChange(field, newVal)}
      />
    </>
  );
}

// Dev Tools
NodeField.displayName = 'NodeField';
